import React, { useCallback, Suspense, useEffect, useState } from 'react';
import { Box, useToast } from '@chakra-ui/react';
import MigrationHelper from './components/MigrationHelper';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { UserProvider } from './context/UserContext';
import { AuctionProvider } from './context/AuctionContext';
import { DiscordProvider } from './context/DiscordContext';
import { useWallet } from '@solana/wallet-adapter-react';
import NavBarWithBalance from './components/NavBarWithBalance';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import PageTransition from './components/PageTransition';
import WalletStatus from './components/WalletStatus';
import ProtectedRoute from './components/ProtectedRoute';
import WhitelistGuard from './components/WhitelistGuard';
import { WhitePaperSkeleton } from './components/LoadingSkeletons';
import ModularUserProfile from './pages/UserProfile/ModularUserProfile';
import ModularProfileRedirect from './pages/UserProfile/ModularProfileRedirect';

// Lazy load components
const Home = React.lazy(() => import('./pages/Home/Home'));
const Game = React.lazy(() => import('./pages/Game/Game'));
const MainCardGame = React.lazy(() => import('./components/MainCardGame'));
const PitchDeck = React.lazy(() => import('./pages/PitchDeck/PitchDeck'));
const WhitePaper = React.lazy(() => import('./pages/WhitePaper/WhitePaper'));
const FAQ = React.lazy(() => import('./pages/FAQ/FAQ'));
const PolicyPage = React.lazy(() => import('./pages/Policy/Policy'));
const HowToBuy = React.lazy(() => import('./pages/HowToBuy/HowToBuy'));
const UserDashboard = React.lazy(() => import('./components/UserDashboard/UserDashboardWithBalance'));
const Auction = React.lazy(() => import('./pages/Auction'));
const DAODashboard = React.lazy(() => import('./pages/DAODashboard/DAODashboard'));
const AngelsKeyNFTPage = React.lazy(() => import('./pages/AngelsKeyNFT/AngelsKeyNFTPage'));
const UserProfile = React.lazy(() => import('./pages/UserProfile/UserProfile'));
const DiscordCallback = React.lazy(() => import('./pages/DiscordCallback'));
const MegaJINUScratchDashboard = React.lazy(() => import('./pages/MegaJINUScratchDashboard'));

// New RESTful game routes
const GamesLayout = React.lazy(() => import('./layouts/GamesLayout'));
const GamesDashboard = React.lazy(() => import('./game/components/GamesDashboard'));
const ScratchCardMarketplace = React.lazy(() => import('./game/components/ScratchCards/ScratchCardMarketplace'));
const ScratchCardGame = React.lazy(() => import('./game/components/ScratchCards/ScratchCardGame'));
const QuickCashGame = React.lazy(() => import('./game/components/ScratchCards/Games/QuickCash/QuickCashGame'));
const GameCreationWizard = React.lazy(() => import('./game/components/ScratchCards/GameCreation/GameCreationWizard'));
// Test components removed

function AppContent() {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const { connected } = useWallet();
    const [isKeyVerified, setIsKeyVerified] = useState(false);

    // Check if the current route is a page that should use NavbarFix instead of the regular navbar
    const shouldUseNavbarFix = 
        location.pathname === '/game' || 
        location.pathname.startsWith('/modular-profile');

    const handleBuyWithCard = useCallback(() => {
        toast({
            title: "Coming Soon",
            description: "Card payments will be available when presale starts",
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top-right",
        });
    }, [toast]);

    return (
        <ErrorBoundary>
            <UserProvider>
                <AuctionProvider>
                    <DiscordProvider>
                        {!shouldUseNavbarFix && (
                            <>
                                <NavBarWithBalance />
                                <WalletStatus />
                                <MigrationHelper onMigrationComplete={(result) => {
                                    toast({
                                        title: "Migration Complete",
                                        description: `Successfully migrated ${result.games} games and ${result.cards} cards to the new system.`,
                                        status: "success",
                                        duration: 5000,
                                        isClosable: true,
                                    });
                                }} />
                            </>
                        )}
                        <AnimatePresence mode="wait">
                            <Suspense fallback={<WhitePaperSkeleton />}>
                                <PageTransition>
                                    <Routes location={location} key={location.pathname}>
                                        <Route path="/" element={
                                            <Layout>
                                                <Home onBuyWithCard={handleBuyWithCard} />
                                            </Layout>
                                        } />
                                        <Route path="/game" element={
                                            <ProtectedRoute requireWallet>
                                                <Game />
                                            </ProtectedRoute>
                                        } />
                                        <Route path="/auction" element={
                                            <WhitelistGuard>
                                                <Layout>
                                                    <Auction />
                                                </Layout>
                                            </WhitelistGuard>
                                        } />
                                        <Route path="/dao" element={
                                            <Layout>
                                                <DAODashboard />
                                            </Layout>
                                        } />
                                        <Route path="/whitepaper" element={
                                            <Layout>
                                                <WhitePaper />
                                            </Layout>
                                        } />
                                        <Route path="/pitch-deck" element={
                                            <Layout>
                                                <PitchDeck />
                                            </Layout>
                                        } />
                                        <Route path="/faq" element={
                                            <Layout>
                                                <FAQ />
                                            </Layout>
                                        } />
                                        <Route path="/policy" element={
                                            <Layout>
                                                <PolicyPage />
                                            </Layout>
                                        } />
                                        <Route path="/how-to-buy" element={
                                            <Layout>
                                                <HowToBuy />
                                            </Layout>
                                        } />
                                        <Route path="/dashboard" element={
                                            <Layout>
                                                <ProtectedRoute requireWallet>
                                                    <UserDashboard />
                                                </ProtectedRoute>
                                            </Layout>
                                        } />
                                        <Route path="/angels-key" element={
                                            <Layout>
                                                <AngelsKeyNFTPage />
                                            </Layout>
                                        } />
                                        {/* ModularProfile Routes */}
                                        <Route path="/profile" element={<Navigate to="/modular-profile" replace />} />
                                        <Route path="/modular-profile" element={<ModularUserProfile />} />
                                        <Route path="/modular-profile/dashboard" element={<ModularUserProfile initialSection="dashboard" />} />
                                        <Route path="/modular-profile/games/*" element={<ModularUserProfile initialSection="games" />} />
                                        <Route path="/modular-profile/nftlootboxes/*" element={<ModularUserProfile initialSection="nftlootboxes" />} />
                                        <Route path="/modular-profile/raffles/*" element={<ModularUserProfile initialSection="raffles" />} />
                                        <Route path="/modular-profile/pawnshop/*" element={<ModularUserProfile initialSection="pawnshop" />} />
                                        <Route path="/modular-profile/marketplace/*" element={<ModularUserProfile initialSection="marketplace" />} />
                                        <Route path="/modular-profile/dex/*" element={<ModularUserProfile initialSection="dex" />} />
                                        <Route path="/modular-profile/staking/*" element={<ModularUserProfile initialSection="staking" />} />
                                        <Route path="/modular-profile/dao/*" element={<ModularUserProfile initialSection="dao" />} />
                                        <Route path="/modular-profile/affiliate/*" element={<ModularUserProfile initialSection="affiliate" />} />
                                        
                                        {/* Redirect handler for old query parameter URLs */}
                                        <Route path="/modular-profile-redirect" element={<ModularProfileRedirect />} />
                                        <Route path="/discord-callback" element={
                                            <PageTransition>
                                                <Suspense fallback={<WhitePaperSkeleton />}>
                                                    <DiscordCallback />
                                                </Suspense>
                                            </PageTransition>
                                        } />
                                        <Route path="/card-game" element={
                                            <Layout>
                                                <MainCardGame />
                                            </Layout>
                                        } />
                                        <Route path="/bounty-pool" element={
                                            <Layout>
                                                <MegaJINUScratchDashboard />
                                            </Layout>
                                        }/>
                                        
                                        {/* Test routes removed */}
                                        
                                        {/* Game routes have been moved to modular profile */}
                                        {/* Routes below are redirected in AppRoutes.jsx */}
                                        
                                        {/* Removed legacy scratch card routes */}
                                    </Routes>
                                </PageTransition>
                            </Suspense>
                        </AnimatePresence>
                    </DiscordProvider>
                </AuctionProvider>
            </UserProvider>
        </ErrorBoundary>
    );
}

export default AppContent;
